import React from 'react';
import { Box, Paper, Typography, Modal, IconButton } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { isViewOnly } from '../utils/permissions';
import EventNoteIcon from '@mui/icons-material/EventNote';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTimezone } from "../contexts/TimezoneContext";
import { formatInTimezone } from "../utils/datetime";

interface Note {
  id: number;
  full_text?: string | null;
  dateAdded: string;
  photoUrl?: string | null;
  lat: number;
  lng: number;
}

interface NotePopoverProps {
  note: Note | null;
  anchorPosition: { top: number; left: number } | null;
  onClose: () => void;
  onDelete: (noteId: string | number) => void;
}

// Helper function to check if a value exists and is not undefined
const hasValue = (value: any): boolean => {
  return value !== undefined && value !== null && value !== '';
};

const NotePopover: React.FC<NotePopoverProps> = ({
  note,
  anchorPosition,
  onClose,
  onDelete,
}) => {
  const open = Boolean(note && anchorPosition);
  const { selectedTimezone } = useTimezone();
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);

  if (!note) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Paper sx={{ p: 3, maxWidth: 800, width: '90vw', maxHeight: 'calc(var(--vh100) * 0.8)', overflow: 'auto', bgcolor: '#212b37', color: 'white', outline: 'none' }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
          <EventNoteIcon sx={{ mr: 1, fontSize: 18 }} />
          Note - {formatInTimezone(note.dateAdded, selectedTimezone, "DD MMM YYYY")}
        </Typography>
        
        {hasValue(note.photoUrl) && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <img 
              src={note.photoUrl || ''} 
              alt="note" 
              style={{ 
                maxWidth: 700, 
                maxHeight: 500, 
                borderRadius: 8,
                objectFit: 'cover',
                width: '100%'
              }} 
            />
          </Box>
        )}
        
        {hasValue(note.full_text) && (
          <Typography variant="body2" sx={{ mb: 1 }}>
            {note.full_text}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
          <Typography variant="caption" sx={{ color: '#aaa' }}>
            {formatInTimezone(note.dateAdded, selectedTimezone, "DD MMM YYYY, h:mm A")}
          </Typography>
          
          {!viewOnly && (
            <IconButton
              size="small"
              onClick={() => onDelete(note.id)}
              sx={{ color: '#ff5252' }}
              title="Delete Note"
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </Paper>
    </Modal>
  );
};

export default NotePopover;
